import { GroupIcon } from "assests/GmsIcons";
import React, { useEffect, useState, useRef } from "react";
import useClickOutside from "Components/GMS/CustomHooks/useClickOutside";

type optionType = {
  label: string;
  value: string;
};
type propType = {
  id: string;
  onChange: Function;
  value: string;
  options: optionType[];
  name: string;
  disabled?: boolean;
  isSearchable?: boolean;
};
const ItemImportDropdown = (props: propType) => {
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<string>("Select");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<optionType[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const { showMenu, ref, handleClick } = useClickOutside();
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const option = props?.options?.filter(
      (x: any) => x?.value === props?.value
    );
    setSelectedOptionLabel(option[0]?.label);
  }, [props?.value, props?.options]);

  useEffect(() => {
    setFilteredOptions(
      props?.options?.filter(
        (option) =>
          option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, props.options]);

  useEffect(() => {
    if (showMenu && selectedIndex !== -1 && menuRef.current) {
      const selectedOptionElement = menuRef.current.childNodes[
        selectedIndex
      ] as HTMLElement;
      selectedOptionElement?.scrollIntoView({ block: "center" });
    }
  }, [showMenu, selectedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showMenu) {
      if (e.key === "ArrowUp" && selectedIndex > 0) {
        setSelectedIndex(selectedIndex - 1);
      } else if (
        e.key === "ArrowDown" &&
        selectedIndex < filteredOptions.length - 1
      ) {
        setSelectedIndex(selectedIndex + 1);
      } else if (e.key === "Enter" && selectedIndex !== -1) {
        const selectedValue = filteredOptions[selectedIndex]?.value;
        props.onChange(selectedValue);
        handleClick();
      }
    }
  };

  /* Re-themed in place 2026-09-02 — same DOM, handlers, ids and testids; only the
     colour classes changed, so the search / arrow-key / outside-click behaviour is
     untouched. TODO(design-system): this hand-rolled dropdown is queued to become
     the DS `Dropdown` (which brings Escape, focus restore and portalling) in the
     multi-choice cluster of the Guide migration — swap it there, not here.
     Dead classes removed rather than translated: `tw-bg-blue` and `tw-text-blue-800`
     never emitted CSS (the palette defines blue 100/500/900 only, no DEFAULT/800) —
     the trigger's blue actually came from ambient legacy styles, so the accent
     token now names it explicitly. */
  return (
    <React.Fragment>
      <div id={props.id} className="tw-flex tw-w-full">
        <div
          id={`${props.id}-container`}
          ref={ref}
          className={`tw-relative ${props?.disabled ? "tw-bg-surface-sunken" : "tw-bg-surface"} tw-w-full`}
          onKeyDown={handleKeyDown}
        >
          <div role="button" data-testid="components-itemimportdropdown-div-1"
            id={`${props.id}-trigger`}
            onClick={props?.disabled ? () => {} : handleClick}
            className={`${showMenu ? "tw-bg-surface-hover" : ""} ${
              props?.disabled ? "" : "hover:tw-bg-surface-hover"
            }  tw-border tw-border-edge-strong tw-p-2  tw-rounded-[4px] tw-flex flex-row tw-items-center tw-justify-between tw-gap-[6px] tw-w-full`}
          >
            <button data-testid="components-itemimportdropdown-button-1"
              id={`${props.id}-select-btn`}
              disabled={props?.disabled}
              className={`tw-text-accent tw-text-sm tw-font-normal tw-w-auto`}
            >
              <span id={`${props.id}-selected-text`} className="tw-ms-2 tw-text-accent tw-text-sm tw-line-clamp-1 tw-text-start">
                {selectedOptionLabel ? selectedOptionLabel : props?.name}
              </span>
            </button>
            {/* GmsIcons hardcodes stroke="#0747A6" on this glyph; a stylesheet rule
                outranks an SVG presentation attribute, so stroke-current re-inks it
                without touching the shared icon file. */}
            <span aria-hidden="true" className="tw-text-accent [&_path]:tw-stroke-current">
              <GroupIcon />
            </span>
          </div>
          {showMenu && (
            <div
              id={`${props.id}-menu`}
              ref={menuRef}
              className="tw-absolute tw-z-50 tw-w-auto tw-bg-surface-raised tw-border tw-border-edge tw-shadow-ds-md tw-pr-4 tw-rounded-bl tw-rounded-br tw-overflow-y-auto tw-max-h-[300px]"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              {props?.isSearchable && (
                <input data-testid="components-itemimportdropdown-input-2"
                  id={`${props.id}-search`}
                  type="text"
                  placeholder="Search..."
                  className="tw-w-full tw-h-full tw-px-2.5 tw-py-2 tw-ml-2 tw-mt-2 tw-bg-surface tw-text-ink placeholder:tw-text-ink-secondary tw-rounded tw-border tw-border-edge-strong tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex"
                  value={searchTerm}
                  onChange={handleInputChange}
                />
              )}
              {filteredOptions.length > 0 ? filteredOptions?.map((option: optionType, index: number) => (
                <div role="button" data-testid="components-itemimportdropdown-div-2"
                  id={`${props.id}-option-${index}`}
                  key={index}
                  onClick={() => {
                    props?.onChange(option?.value);
                    handleClick();
                  }}
                  className={`tw-w-full tw-flex tw-justify-between tw-items-center tw-py-2 tw-pr-2.5 hover:tw-bg-surface-hover ${
                    selectedIndex === index ? "tw-bg-accent-tint" : ""
                  }`}
                >
                  <label 
                    id={`${props.id}-option-label-${index}`}
                    className={`tw-ms-2 tw-text-ink tw-text-sm`}
                  >
                    {option?.label}
                  </label>
                </div>
              )) : (
                <div
                  id={`${props.id}-no-results`}
                  className={`tw-w-full tw-flex tw-justify-between tw-items-center tw-py-2 tw-pr-2.5`}
                >
                  <label 
                    id={`${props.id}-no-results-text`}
                    className={`tw-ms-2 tw-text-ink tw-text-sm`}
                  >
                    Not found
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ItemImportDropdown;
